import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CalendarView } from '../CalendarView';
import { WeekNavigation } from '../WeekNavigation';
import { UnscheduledList } from '../UnscheduledList';
import { usePlanningData } from '../../hooks/usePlanningData';
import { useWeekNavigation } from '../../hooks/useWeekNavigation';
import { Loading } from '@/ui/components/atoms/Loading';
import { PlanningExpertise } from '../../types/planning.types';
import './PlanningView.styles.css';

export const PlanningView: React.FC = () => {
  const navigate = useNavigate();
  const { expertises, expertName, stats, loading, error, firstWeekWithAppointment } = usePlanningData();
  const { currentWeek, setCurrentWeek, getWeekDates } = useWeekNavigation();

  // Set initial week to first week with appointment
  useEffect(() => {
    if (firstWeekWithAppointment !== null && firstWeekWithAppointment !== 0) {
      setCurrentWeek(firstWeekWithAppointment);
    }
  }, [firstWeekWithAppointment, setCurrentWeek]);

  const handleExpertiseClick = (expertise: PlanningExpertise) => {
    navigate(`/expert/affaire/${expertise.id}`, {
      state: { from: '/expert/planning' }
    });
  };

  if (loading) {
    return (
      <div className="planning-page">
        <div className="planning-page__loading">
          <Loading />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="planning-page">
        <div className="planning-page__error">
          Erreur lors du chargement: {(error as Error).message}
        </div>
      </div>
    );
  }

  const weekDates = getWeekDates();
  const unscheduledExpertises = expertises.filter(e => !e.date_du_rv);

  return (
    <div className="planning-page">
      {/* Custom header with stats */}
      <div className="planning-page__header">
        <div className="planning-page__header-content">
          <h1 className="planning-page__title">
            Planning
          </h1>
          <p className="planning-page__subtitle">
            Gérez vos rendez-vous et visites
          </p>
        </div>
        
        <div className="planning-page__actions">
          {stats && (
            <>
              <span className="planning-stat">
                À visiter: <strong>{stats.a_visiter}</strong>
              </span>
              <span className="planning-stat">
                Planifiées: <strong>{stats.visites_planifiees}</strong>
              </span>
            </>
          )}
        </div>
      </div>
      
      {/* Week navigation */}
      <div className="planning-page__navigation">
        <WeekNavigation 
          currentWeek={currentWeek}
          onWeekChange={setCurrentWeek}
        />
      </div>
      
      {/* Calendar view */}
      <div className="planning-page__content">
        <CalendarView 
          weekDates={weekDates}
          expertises={expertises}
          onExpertiseClick={handleExpertiseClick}
        />
        
        {/* Unscheduled expertises */}
        {unscheduledExpertises.length > 0 && (
          <UnscheduledList 
            expertises={unscheduledExpertises}
            onExpertiseClick={handleExpertiseClick}
          />
        )}
      </div>
    </div>
  );
};